import styled from '@emotion/styled';
import typography from 'storybookConfig/mixins/typography';
import Avatar from 'storybookConfig/stories/cells/Avatar';
import Icon from 'storybookConfig/stories/molecules/Icon';
import { CommerceType } from 'types/models/company';

export const COMMERCE_TYPE_AVATAR_SIZE = '40px';

interface CommerceTypeAvatarProps {
  commerceType: CommerceType;
  /**
   * `compact` is the rounded-square avatar of the sidebar's company picker
   */
  variant?: 'default' | 'compact';
}

const getCommerceTypeDisplayLabel = (commerceType: CommerceType): string => {
  if (commerceType === CommerceType.Buyer) return 'Retailer';
  if (commerceType === CommerceType.Seller) return 'Supplier';
  return '';
};

/* Iris for both roles: the icon (building or store) and the label tell them apart. The muted tint,
   not the subtle one, so the tile still shows on the picker's grey pill. */
const CompactTile = styled.div<{ $hasRole: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme, $hasRole }) =>
    $hasRole ? theme.color.accentMuted : theme.color.surfaceStrong};
`;

/* The role, in a pill set into the tile's bottom edge. */
const CompactLabel = styled.span`
  position: absolute;
  top: 28px;
  left: 50%;
  transform: translateX(-50%);
  padding: 0 4px;
  border: 1px solid ${({ theme }) => theme.color.surfaceCanvas};
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.color.accentMuted};
  color: ${({ theme }) => theme.color.accentStrong};
  white-space: nowrap;
  ${typography('Other/Micro Label')};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const CommerceTypeAvatar = ({ commerceType, variant = 'default' }: CommerceTypeAvatarProps) => {
  const label = getCommerceTypeDisplayLabel(commerceType);

  if (variant === 'compact') {
    return (
      <CompactTile $hasRole={!!label}>
        <Icon
          name={commerceType === CommerceType.Seller ? 'store' : 'building'}
          color={label ? 'accentHover' : 'bodyTextCaption'}
          size="18px"
        />
        {label && <CompactLabel>{label}</CompactLabel>}
      </CompactTile>
    );
  }

  return (
    <Avatar
      iconName={commerceType === CommerceType.Buyer ? 'building' : 'store'}
      text={label}
      backgroundColor={commerceType === CommerceType.Buyer ? 'accentSubtle' : 'accentMuted'}
      color={commerceType === CommerceType.Buyer ? 'accentHover' : 'accentStrong'}
      borderColor={commerceType === CommerceType.Buyer ? 'accentBorder' : 'accentMuted'}
      size={COMMERCE_TYPE_AVATAR_SIZE}
    />
  );
};

export default CommerceTypeAvatar;
